<script lang="ts">
  import { DEFAULT_CAMPAIGN_COLOR } from '$lib/constants';
  import { cn } from '$lib/utils';
  import { getAnchorFromCmsLink, getImageAttributes } from '$lib/utils/cms';
  import type { PromotionalBlockStoryblok, PromotionalMartekingCardStoryblok } from '$types/bloks';
  import Badge from './ui/badge.svelte';
  import Button from './ui/button.svelte';
  import Image from '$components/image.svelte';
  import { getCampaignColors } from '$lib/stores/campaigns';
  import { buildSrcSet } from '$lib/image';

  export let block: PromotionalBlockStoryblok | PromotionalMartekingCardStoryblok;
  export let variant: 'md' | 'sm' | 'lg' = 'md';
  let className: string | undefined = undefined;
  export { className as class };

  export let color: number | string = DEFAULT_CAMPAIGN_COLOR;
  export let isFirst: boolean | undefined = undefined;
  export let isLast: boolean | undefined = undefined;

  let backgroundColor: string | undefined = undefined;
  let textColor: string | undefined = undefined;

  let badgeBackgroundColor: string | undefined = undefined;
  let badgeTextColor: string | undefined = undefined;
  let isBlackCampaignColor: boolean;
  let isBlackColorBadge: boolean;

  $: if (color) {
    const campaignColors = getCampaignColors(color);
    backgroundColor = campaignColors.backgroundColor;
    textColor = campaignColors.textColor;
    isBlackCampaignColor = campaignColors.isBlackColor;
  }

  $: if (block.badge_colors) {
    const badgeCampaignColors = getCampaignColors(block.badge_colors);
    badgeBackgroundColor = badgeCampaignColors.backgroundColor;
    badgeTextColor = badgeCampaignColors.textColor;
    isBlackColorBadge = badgeCampaignColors.isBlackColor;
  }

  $: cardLink = block.cta_link ? $getAnchorFromCmsLink(block.cta_link).href : null;

  $: showAsHighlight = block.show_as_highlight && variant == 'sm' && isFirst;
</script>

<svelte:element
  this={cardLink ? 'a' : 'div'}
  href={cardLink}
  data-sveltekit-noscroll={false}
  style:--backgroundColor={backgroundColor}
  style:--textColor={textColor}
  style:--badgeBackgroundColor={badgeBackgroundColor}
  style:--badgeTextColor={badgeTextColor}
  class={cn(
    'relative flex lg:items-center rounded-md lg:gap-2 gap-4 justify-center p-2.5 pb-2 lg:p-3 lg:pb-3 flex-col lg:flex-row',
    variant === 'sm' && 'lg:flex-col lg:gap-4 lg:p-2.5 lg:pb-2 lg:items-start justify-between',
    variant === 'lg' && 'lg:flex-col lg:gap-4 lg:p-3  lg:items-start justify-between',
    !cardLink ? 'pointer-events-none' : '',
    // new colors from configuration
    'bg-[var(--backgroundColor)] text-[var(--textColor)]',
    isFirst && block.image?.filename && 'text-highlight bg-transparent',
    showAsHighlight && 'min-h-30 lg:min-h-0',
    className
  )}
>
  {#if block.show_badge_instead_of_discount_icon}
    <Badge
      class={cn(
        'bg-[--badgeBackgroundColor] text-[--badgeTextColor]',
        isBlackColorBadge && 'bg-accent'
      )}
      size="micro">{block.badge_text}</Badge
    >
  {:else}
    <svg
      width="16"
      height="17"
      viewBox="0 0 16 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      class={cn('shrink-0', variant == 'lg' && 'w-6 h-6')}
    >
      <path
        d="M6.24626 1.47545C6.42665 1.25626 6.51685 1.14666 6.59835 1.06768C7.37951 0.310773 8.62049 0.310773 9.40165 1.06768C9.48316 1.14666 9.57335 1.25626 9.75374 1.47545C9.83841 1.57834 9.88074 1.62978 9.92369 1.67578C10.3211 2.10144 10.8832 2.33429 11.4652 2.3143C11.5281 2.31214 11.5944 2.3057 11.727 2.29282C12.0096 2.26538 12.1508 2.25165 12.2643 2.25344C13.3519 2.27059 14.2294 3.1481 14.2466 4.23567C14.2483 4.34915 14.2346 4.49043 14.2072 4.77298C14.1943 4.9056 14.1879 4.9719 14.1857 5.0348C14.1657 5.61678 14.3986 6.17893 14.8242 6.57632C14.8702 6.61926 14.9217 6.66159 15.0245 6.74626C15.2437 6.92665 15.3533 7.01684 15.4323 7.09835C16.1892 7.87951 16.1892 9.12049 15.4323 9.90165C15.3533 9.98316 15.2437 10.0734 15.0245 10.2537C14.9217 10.3384 14.8702 10.3807 14.8242 10.4237C14.3986 10.8211 14.1657 11.3832 14.1857 11.9652C14.1879 12.0281 14.1943 12.0944 14.2072 12.227C14.2346 12.5096 14.2483 12.6508 14.2466 12.7643C14.2294 13.8519 13.3519 14.7294 12.2643 14.7466C12.1508 14.7483 12.0096 14.7346 11.727 14.7072C11.5944 14.6943 11.5281 14.6879 11.4652 14.6857C10.8832 14.6657 10.3211 14.8986 9.92369 15.3242C9.88074 15.3702 9.83841 15.4217 9.75374 15.5245C9.57335 15.7437 9.48316 15.8533 9.40165 15.9323C8.62049 16.6892 7.37951 16.6892 6.59835 15.9323C6.51684 15.8533 6.42665 15.7437 6.24626 15.5245C6.16159 15.4217 6.11926 15.3702 6.07632 15.3242C5.67893 14.8986 5.11678 14.6657 4.5348 14.6857C4.4719 14.6879 4.4056 14.6943 4.27298 14.7072C3.99043 14.7346 3.84915 14.7483 3.73567 14.7466C2.6481 14.7294 1.77059 13.8519 1.75344 12.7643C1.75165 12.6508 1.76538 12.5096 1.79282 12.227C1.8057 12.0944 1.81214 12.0281 1.8143 11.9652C1.83429 11.3832 1.60144 10.8211 1.17578 10.4237C1.12978 10.3807 1.07834 10.3384 0.975454 10.2537C0.756259 10.0734 0.646661 9.98316 0.567682 9.90165C-0.189227 9.12049 -0.189227 7.87951 0.567682 7.09835C0.646661 7.01685 0.756258 6.92665 0.975454 6.74626C1.07834 6.66159 1.12978 6.61926 1.17578 6.57632C1.60144 6.17893 1.83429 5.61678 1.8143 5.0348C1.81214 4.97191 1.8057 4.9056 1.79282 4.77298C1.76538 4.49043 1.75165 4.34915 1.75344 4.23567C1.77059 3.1481 2.6481 2.27059 3.73567 2.25344C3.84915 2.25165 3.99043 2.26538 4.27298 2.29282C4.4056 2.3057 4.47191 2.31214 4.5348 2.3143C5.11678 2.33429 5.67893 2.10144 6.07632 1.67578C6.11926 1.62978 6.16159 1.57834 6.24626 1.47545Z"
        class={cn(
          // new colors
          'fill-[var(--textColor)]',
          isBlackCampaignColor && 'fill-accent'
        )}
      />
      <path
        d="M5.91875 12.1045C5.85031 12.1045 5.82097 12.0556 5.85031 11.9872L9.15519 5.32853C9.17475 5.27964 9.22364 5.26008 9.27253 5.26008H10.0743C10.1428 5.26008 10.1721 5.30897 10.1428 5.37742L6.83786 12.0361C6.81831 12.085 6.76942 12.1045 6.72053 12.1045H5.91875ZM3.71875 7.23519C3.71875 5.89564 4.29564 5.20142 5.42008 5.20142C6.54453 5.20142 7.12142 5.89564 7.12142 7.23519C7.12142 8.58453 6.53475 9.27875 5.42008 9.27875C4.29564 9.27875 3.71875 8.57475 3.71875 7.23519ZM4.67697 7.23519C4.67697 8.14453 4.89208 8.52586 5.42008 8.52586C5.93831 8.52586 6.16319 8.14453 6.16319 7.23519C6.16319 6.3063 5.93831 5.92497 5.42008 5.92497C4.89208 5.92497 4.67697 6.3063 4.67697 7.23519ZM8.87164 10.1196C8.87164 8.78008 9.44853 8.08586 10.573 8.08586C11.6974 8.08586 12.2743 8.78008 12.2743 10.1196C12.2743 11.469 11.6876 12.1632 10.573 12.1632C9.44853 12.1632 8.87164 11.4592 8.87164 10.1196ZM9.82986 10.1196C9.82986 11.029 10.045 11.4103 10.573 11.4103C11.0912 11.4103 11.3161 11.029 11.3161 10.1196C11.3161 9.2103 11.0912 8.83875 10.573 8.83875C10.045 8.83875 9.82986 9.2103 9.82986 10.1196Z"
        class={cn(
          // new colors
          'fill-[var(--backgroundColor)]',
          isBlackCampaignColor && 'fill-[var(--textColor)]'
        )}
      />
    </svg>
  {/if}

  <div class={cn('flex justify-between w-full items-end gap-2', variant === 'md' && 'w-fit')}>
    <p
      class={cn(
        'flex flex-col lg:inline',
        (variant === 'sm' || variant === 'lg') && 'lg:flex lg:flex-col',
        variant == 'lg' && 'gap-2'
      )}
    >
      <span
        class={cn(
          'text-sm lg:text-lg',
          variant === 'sm' && 'lg:text-sm',
          variant === 'lg' && 'text-4xl lg:text-4xl'
        )}>{block.title}</span
      >

      <span
        class={cn(
          'text-xl font-semibold lg:text-lg',
          showAsHighlight && 'text-3xl lg:text-[28px]',
          !showAsHighlight && variant === 'sm' && 'lg:text-xl lg:font-semibold',
          variant === 'lg' && 'text-4xl lg:text-4xl font-semibold lg:font-semibold'
        )}>{block.subtitle}</span
      >
    </p>
    {#if (variant === 'sm' || variant === 'lg') && isLast && block.show_arrow}
      <Button
        icon="arrow-right"
        size="xs"
        class={cn(
          'bg-[--textColor] text-[--backgroundColor] hover:opacity-96 hover:bg-[--textColor]/96',
          isBlackCampaignColor && 'bg-accent text-highlight hover:bg-accent/96'
        )}
      />
    {/if}
  </div>
  {#if isFirst && block.image?.filename}
    <div class="absolute inset-0 -z-10 bg-primary/8 w-full h-full rounded-md"></div>
    <Image
      loading="eager"
      class="absolute inset-0 -z-20 h-full w-full transform-gpu rounded-md object-cover overflow-hidden"
      srcset={buildSrcSet({
        loader: ({ width }) => getImageAttributes(block.image, { size: [width, 0] }).src,
        widths: [320, 640]
      })}
    />
  {/if}
</svelte:element>
